import React, { useEffect, useState, useMemo } from "react";
import DynamicRenderer, { type DynamicRendererProps } from "./DynamicRenderer";
import { fetchDynamicConfig } from "../../../api/fetchDynamicConfig";
import { useGenericListStore } from "../../../store/useGenericListStore";
import { usePubSubStore } from "../../../store/usePubSubStore";
import { Form } from "../Form/Form";
import List from "../List/List";
import { z } from "zod";
import { useToast } from "../Toast/ToastContext";
import clsx from "clsx";
// Importe todos os types necessários de Form
import type {
  FormProps,
} from "../Form/Form";
import type {
  FormField, // <-- import FormField type
} from "../Form/FormFields";
import type { ListProps } from "../List/List";
import type { ChartProps } from "../Chart/Chart";

// Define the DynamicComponentConfig type
export type DynamicComponentConfig = {
  id?: string | number;
  type: "card" | "modal" | string;
  position?: {
    column: number;
    row: number;
  };
  content: {
    component: string;
    props?: Record<string, unknown>;
    [key: string]: unknown;
  };
};

// Define the DynamicContext type
type DynamicContext = {
  store: {
    getList: (key: string) => string[];
    addItem: (key: string, value: string) => void;
    setList: (key: string, values: string[]) => void;
  };
  pubsub?: {
    subscribe: (event: string, cb: (...args: unknown[]) => void) => void;
    publish: (event: string, ...args: unknown[]) => void;
  };
  theme: "light" | "dark";
};

// Define the ComponentMap type if not already imported
type ComponentMap = Record<string, React.ComponentType<Record<string, unknown>>>;

const Chart = React.lazy(() => import("../Chart/Chart"));

const THEME_CLASSNAMES = {
  light: "bg-white text-neutral-900",
  dark: "bg-neutral-900 text-neutral-100",
};

const defaultComponentMap: ComponentMap = {
  form: (props) => {
    const { context, theme = "light", ...formProps } = props;
    // PATCH: sobrescreva props explicitamente
    const {
      fieldPropsOverride,
      ...rest
    } = formProps as unknown as FormProps;
    // Access formPropsOverride from props if available
    const formPropsOverride = (props as { formPropsOverride?: Partial<FormProps> }).formPropsOverride || {};
    return (
      <div className={clsx("fluent-surface", THEME_CLASSNAMES[theme === "dark" ? "dark" : "light"])}>
        <Form
          {...(rest as FormProps)}
          {...formPropsOverride}
          fieldPropsOverride={fieldPropsOverride}
          theme={theme as "light" | "dark" | undefined}
          onSubmit={(data: Record<string, unknown>) => {
            const typedContext = context as DynamicContext;
            // If you want to support onSubmitList, ensure it's passed via props
            const onSubmitList = (props as { onSubmitList?: string }).onSubmitList;
            if (
              typeof onSubmitList === "string" &&
              typeof data.nome === "string" &&
              data.nome.trim()
            ) {
              typedContext.store.addItem(onSubmitList, data.nome.trim());
            }
            typedContext.pubsub?.publish("userCreated", data);
            const onSubmitFn = ((rest as unknown) as FormProps).onSubmit;
            if (typeof onSubmitFn === "function") {
              onSubmitFn(data);
            }
          }}
        />
      </div>
    );
  },
  list: (props) => {
    const { context, theme = "light", ...listProps } = props;
    const patchedListProps = {
      ...listProps,
      fetchPage: typeof listProps.fetchPage === "function"
        ? (async (pageIndex: number, pageSize: number) => {
            const fetchPageFn = listProps.fetchPage as (pageIndex: number, pageSize: number) => Promise<unknown>;
            const result = await fetchPageFn(pageIndex, pageSize);
            return Array.isArray(result)
              ? result.map((item) => (typeof item === "object" && item !== null ? item as Record<string, unknown> : {}))
              : [];
          })
        : undefined,
    };
    return (
      <div className={clsx("fluent-surface", THEME_CLASSNAMES[theme === "dark" ? "dark" : "light"])}>
        <List
          {...(patchedListProps as ListProps<Record<string, unknown>>)}
          theme={theme as "light" | "dark" | undefined}
          data={
            typeof listProps.listKey === "string"
              ? (context as DynamicContext).store.getList(listProps.listKey).map((nome: string) => ({ nome }))
              : (listProps.data as ListProps<Record<string, unknown>>["data"])
          }
          onDataChange={(data: Record<string, unknown>[]) => {
            if (typeof listProps.listKey === "string") {
              (context as DynamicContext).store.setList(
                listProps.listKey,
                data.map((item) => (typeof item.nome === "string" ? item.nome : ""))
              );
            }
            if (typeof listProps.onDataChange === "function") {
              listProps.onDataChange(data);
            }
          }}
        />
      </div>
    );
  },
  chart: (props) => {
    const { theme = "light", ...rest } = props;
    return (
      <React.Suspense fallback={<div>Carregando gráfico...</div>}>
        <div className={clsx("fluent-surface", THEME_CLASSNAMES[theme === "dark" ? "dark" : "light"])}>
          <Chart {...(rest as ChartProps)} />
        </div>
      </React.Suspense>
    );
  },
  custom: (props: { render?: (context: unknown) => React.ReactNode; context?: unknown; theme?: "light" | "dark" }) =>
    typeof props.render === "function"
      ? <div className={clsx("fluent-surface", THEME_CLASSNAMES[(props.theme ?? "light") as "light" | "dark"])}>{props.render(props.context)}</div>
      : null,
};

export interface DynamicRendererLoaderProps extends Partial<DynamicRendererProps> {
  componentMap?: ComponentMap;
  config?: DynamicComponentConfig[];
  eventPrefix?: string;
  formProps?: FormProps;
  formPropsOverride?: Partial<FormProps>;
  fieldPropsOverride?: Partial<FormField> | ((field: FormField) => Partial<FormField>);
  listProps?: ListProps<Record<string, unknown>>;
  chartProps?: ChartProps;
  dynamicRendererProps?: Omit<
    DynamicRendererProps,
    "data" | "componentMap" | "columns" | "rows" | "theme" | "responsiveClassName"
  >;
  [key: string]: unknown;
}


const LAYOUT_STORAGE_KEY = "dynamicRendererLayout";

function saveLayoutToStorage(config: DynamicComponentConfig[]) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Intentionally ignore write errors (e.g., private mode)
  }
}

function loadLayoutFromStorage(): DynamicComponentConfig[] | null {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Intentionally ignore JSON parse errors
  }
  return null;
}

const DynamicComponentConfigSchema = z.object({
  type: z.union([z.literal("card"), z.literal("modal"), z.string()]),
  position: z
    .object({
      column: z.number(),
      row: z.number(),
    })
    .optional(),
  content: z.object({
    component: z.string(),
    props: z.record(z.unknown()),
  }).passthrough(),
});
const DynamicComponentConfigArraySchema = z.array(DynamicComponentConfigSchema);

function validateConfig(config: unknown): DynamicComponentConfig[] {
  const result = DynamicComponentConfigArraySchema.safeParse(config);
  if (result.success) {
    return result.data;
  } else {
    console.warn("Configuração dinâmica inválida:", result.error);
    return [];
  }
}

const DynamicRendererLoader: React.FC<DynamicRendererLoaderProps> = (props) => {
  const {
    componentMap,
    config: configProp,
    columns = 3,
    rows = 1,
    theme = "light",
    responsiveClassName = "",
    eventPrefix = "",
    formPropsOverride = {},
    fieldPropsOverride,
    // ...other props
  } = props;

  const safeTheme = theme === "dark" ? "dark" : "light";

  const [config, setConfig] = useState<DynamicComponentConfig[]>(
    () => {
      const initial =
        configProp ??
        loadLayoutFromStorage() ??
        [];
      return validateConfig(initial);
    }
  );

  useEffect(() => {
    saveLayoutToStorage(config);
  }, [config]);

  useEffect(() => {
    if (!configProp && config.length === 0) {
      fetchDynamicConfig().then((data) => {
        setConfig(validateConfig(data));
      });
    }
  }, [configProp, config.length]);

  const addItem = useGenericListStore((state) => state.addItem);
  const setList = useGenericListStore((state) => state.setList);
  const lists = useGenericListStore((state) => state.lists);

  const getList = React.useCallback((key: string) => lists[key] || [], [lists]);

  // Memoiza subscribe e publish para garantir identidade estável
  const subscribe = React.useCallback(
    (event: string, cb: (...args: unknown[]) => void) =>
      usePubSubStore.getState().subscribe(`${eventPrefix}${event}`, cb),
    [eventPrefix]
  );
  const publish = React.useCallback(
    (event: string, ...args: unknown[]) =>
      usePubSubStore.getState().publish(`${eventPrefix}${event}`, ...args),
    [eventPrefix]
  );

  const context: DynamicContext = useMemo(() => ({
    store: { getList, addItem, setList },
    pubsub: { subscribe, publish },
    theme: safeTheme,
  }), [getList, addItem, setList, subscribe, publish, safeTheme]);

  // Memoiza wrapWithContext para evitar recriação desnecessária
  const wrapWithContext = React.useCallback(
    (Comp: React.ComponentType<Record<string, unknown>> | React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>>) => {
      const Wrapped = (props: Record<string, unknown>) => (
        <Comp {...{ ...props, theme: safeTheme }} context={context} />
      );
      Wrapped.displayName = `WithContext(${
        ((Comp as unknown) as { displayName?: string }).displayName ||
        ((Comp as unknown) as { name?: string }).name ||
        "Component"
      })`;
      return Wrapped;
    },
    [context, safeTheme]
  );

  const mergedComponentMap = useMemo(() => {
    const merged: Record<string, React.ComponentType<Record<string, unknown>>> = {};
    const all = { ...defaultComponentMap, ...(componentMap || {}) };
    Object.keys(all).forEach((key) => {
      merged[key] = wrapWithContext(all[key]);
    });
    return merged;
  }, [componentMap, wrapWithContext]);

  const showToast = useToast();

  const data = useMemo(
    () =>
      config.map((item, idx) => {
        const Comp = mergedComponentMap[item.content.component];
        const isFirst = idx === 0; // <-- identifica o primeiro widget
        const { props = {}, open, onClose, ...restContent } = item.content;
        return {
          id: (item as DynamicComponentConfig & { id?: string | number }).id ?? idx,
          type: item.type,
          content: {
            ...restContent,
            ...props,
            open: typeof open === "boolean" ? open : undefined,
            onClose:
              typeof onClose === "function"
                ? ((onClose as (...args: unknown[]) => unknown).length === 0
                    ? (onClose as () => void)
                    : () => {
                        (onClose as (...args: unknown[]) => unknown)();
                      })
                : undefined,
            formPropsOverride,
            fieldPropsOverride,
            body: Comp
              ? (
                  <React.Suspense fallback={<div>Carregando componente...</div>}>
                    <Comp
                      {...props}
                      formPropsOverride={formPropsOverride}
                      fieldPropsOverride={fieldPropsOverride}
                      theme={safeTheme}
                      context={context}
                      autoFocusFirstField={isFirst} // <-- já está passando!
                    />
                  </React.Suspense>
                )
              : (() => {
                  console.warn(`Componente desconhecido: ${item.content.component}`);
                  showToast({
                    type: "warning",
                    title: "Componente desconhecido",
                    description: `O widget "${item.content.component}" não está cadastrado. Consulte a documentação ou registre-o no componentRegistry.`,
                    duration: 6000,
                  });
                  return (
                    <div className="text-red-700 bg-yellow-50 border border-yellow-300 rounded-lg p-4 my-2">
                      <strong className="block mb-1">Componente desconhecido:</strong>
                      <code className="bg-yellow-100 px-1 py-0.5 rounded">{item.content.component}</code>
                      <div className="mt-2 text-sm">
                        Para cadastrar um novo tipo de widget, registre-o no <code className="bg-neutral-100 px-1 rounded">componentRegistry</code> ou adicione ao <code className="bg-neutral-100 px-1 rounded">componentMap</code>.<br />
                        <span className="text-neutral-700">
                          Consulte a documentação ou contate o administrador do sistema para suporte.<br />
                          Exemplo: <code className="bg-neutral-100 px-1 rounded">componentRegistry[&quot;{item.content.component}&quot;] = SeuComponente;</code>
                        </span>
                      </div>
                    </div>
                  );
                })(),
          },
        };
      }),
    [config, mergedComponentMap, context, showToast, safeTheme, formPropsOverride, fieldPropsOverride]
  );

  if (!config.length) {
    return (
      <div className="animate-pulse space-y-4 p-8">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-32 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className={clsx("fluent-background", THEME_CLASSNAMES[safeTheme], responsiveClassName)}>
      <DynamicRenderer
        data={data}
        columns={columns}
        rows={rows}
        theme={safeTheme}
        componentMap={mergedComponentMap}
        responsiveClassName=""
        {...(props.dynamicRendererProps || {})}
      />
    </div>
  );
};

export default DynamicRendererLoader;